import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { SEO } from "@/components/SEO";
import { fetchArticles, deleteArticle, toggleArticleFeatured, coverImageSrc } from "@/lib/api";

export default function AdminDashboard() {
  const [articles, setArticles] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  async function load() {
    try {
      const data = await fetchArticles();
      setArticles(data);
    } catch {
      toast.error("Couldn't load articles.");
      setArticles([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(id, title) {
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    setDeletingId(id);
    try {
      await deleteArticle(id);
      toast.success("Article deleted.");
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Couldn't delete that article.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleToggleFeatured(id, next) {
    setTogglingId(id);
    try {
      await toggleArticleFeatured(id, next);
      toast.success(next ? "Featured at the top of the grid." : "Removed from featured.");
      const refreshed = await fetchArticles();
      setArticles(refreshed);
    } catch {
      toast.error("Couldn't update featured status.");
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div data-testid="admin-dashboard">
      <SEO title="Admin" description="Blueprint4Joy® admin." path="/admin" noindex />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-[#1C1917]">This Curious Life</h1>
          <p className="text-sm text-[#57534E] mt-1">Add, edit, or remove articles from the blog.</p>
        </div>
        <Link
          to="/admin/articles/new"
          data-testid="admin-new-article-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF5A4F] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#e8463c] transition"
        >
          <Plus size={16} /> New Article
        </Link>
      </div>

      {articles === null && (
        <div className="flex items-center gap-2 text-[#57534E] py-16">
          <Loader2 className="animate-spin" size={18} /> Loading articles…
        </div>
      )}

      {articles?.length === 0 && (
        <div data-testid="admin-empty-state" className="text-center py-20 border border-dashed border-[#E7E5E4] rounded-2xl bg-white">
          <p className="text-[#57534E]">No articles yet.</p>
          <Link to="/admin/articles/new" className="inline-block mt-3 text-[#159E9A] font-medium hover:underline">
            Write your first one →
          </Link>
        </div>
      )}

      {articles && articles.length > 0 && (
        <div data-testid="admin-articles-list" className="bg-white border border-[#E7E5E4] rounded-2xl divide-y divide-[#E7E5E4] overflow-hidden">
          {articles.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-article-row-${a.slug}`}
              className="flex items-center gap-4 p-4 md:p-5"
            >
              <div className="w-16 h-16 rounded-lg bg-[#F5F4F1] overflow-hidden flex-shrink-0">
                {coverImageSrc(a) && (
                  <img
                    src={coverImageSrc(a)}
                    alt={a.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-[#1C1917] truncate flex items-center gap-2">
                  {a.title}
                  {a.is_featured && (
                    <span
                      data-testid={`admin-featured-badge-${a.slug}`}
                      className="inline-flex items-center gap-1 text-[10px] tracking-wide uppercase text-[#F6A041] bg-[#F6A041]/10 rounded-full px-2 py-0.5 flex-shrink-0"
                    >
                      <Star size={10} className="fill-[#F6A041]" /> Featured
                    </span>
                  )}
                </p>
                <p className="text-xs text-[#57534E] mt-0.5">
                  {a.author_name} · {a.date}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={() => handleToggleFeatured(a.id, !a.is_featured)}
                  disabled={togglingId === a.id}
                  data-testid={`admin-toggle-featured-${a.slug}`}
                  title={a.is_featured ? "Remove from featured" : "Feature this article"}
                  className={`p-2 rounded-lg transition disabled:opacity-50 ${
                    a.is_featured
                      ? "text-[#F6A041] hover:bg-[#F6A041]/10"
                      : "text-[#57534E] hover:text-[#F6A041] hover:bg-[#F6A041]/10"
                  }`}
                >
                  {togglingId === a.id ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Star size={16} className={a.is_featured ? "fill-[#F6A041]" : ""} />
                  )}
                </button>
                <Link
                  to={`/admin/articles/${a.id}/edit`}
                  data-testid={`admin-edit-${a.slug}`}
                  className="p-2 rounded-lg text-[#57534E] hover:text-[#159E9A] hover:bg-[#159E9A]/10 transition"
                  aria-label="Edit"
                >
                  <Pencil size={16} />
                </Link>
                <button
                  onClick={() => handleDelete(a.id, a.title)}
                  disabled={deletingId === a.id}
                  data-testid={`admin-delete-${a.slug}`}
                  className="p-2 rounded-lg text-[#57534E] hover:text-[#FF5A4F] hover:bg-[#FF5A4F]/10 transition disabled:opacity-50"
                  aria-label="Delete"
                >
                  {deletingId === a.id ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
